import React, {useState} from 'react';
import {
  Collapse,
  Tabs,
  Switch,
  TimePicker,
  Button,
  Row,
  Col,
  Tag,
  Space,
  Popconfirm,
  Alert,
  Tooltip,
} from 'antd';
import {
  ClockCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const format = 'HH:mm';

export interface TimeSlotData {
  start: number; // minutes from midnight (0..1440)
  end: number;   // minutes from midnight (0..1440)
  isOpen?: boolean;
}

export interface DayScheduleData {
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  slots: TimeSlotData[];
}

interface Props {
  weeklyHours: DayScheduleData[];
  timezone?: string;
  isAlwaysOpen?: boolean;
  shopBellEnabled?: boolean;
  isOpenStatus?: boolean;
  onChangeWeeklyHours: (hours: DayScheduleData[]) => void;
  onChangeAlwaysOpen?: (alwaysOpen: boolean) => void;
  onChangeShopBell?: (enabled: boolean) => void;
}

export const DAYS = [
  {dayOfWeek: 1, label: 'Thứ 2', fullLabel: 'Thứ Hai'},
  {dayOfWeek: 2, label: 'Thứ 3', fullLabel: 'Thứ Ba'},
  {dayOfWeek: 3, label: 'Thứ 4', fullLabel: 'Thứ Tư'},
  {dayOfWeek: 4, label: 'Thứ 5', fullLabel: 'Thứ Năm'},
  {dayOfWeek: 5, label: 'Thứ 6', fullLabel: 'Thứ Sáu'},
  {dayOfWeek: 6, label: 'Thứ 7', fullLabel: 'Thứ Bảy'},
  {dayOfWeek: 0, label: 'Chủ Nhật', fullLabel: 'Chủ Nhật'},
];

const PRESETS = [
  {
    name: 'Quán ăn sáng (05:30 - 13:00)',
    slots: [{start: 330, end: 780, isOpen: true}],
  },
  {
    name: 'Sáng & Chiều tối (08:00 - 12:00, 13:30 - 22:00)',
    slots: [
      {start: 480, end: 720, isOpen: true},
      {start: 810, end: 1320, isOpen: true},
    ],
  },
  {
    name: 'Cả ngày (06:00 - 23:00)',
    slots: [{start: 360, end: 1380, isOpen: true}],
  },
  {
    name: '24/24 (00:00 - 24:00)',
    slots: [{start: 0, end: 1440, isOpen: true}],
  },
];

export const minutesToDayjs = (min: number) => {
  if (min === 1440) return dayjs().hour(23).minute(59).second(59);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return dayjs().hour(h).minute(m).second(0);
};

export const dayjsToMinutes = (d: dayjs.Dayjs | null) => {
  if (!d) return 0;
  return d.hour() * 60 + d.minute();
};

export const ShopWeeklyScheduleManager: React.FC<Props> = ({
  weeklyHours,
  timezone = 'Asia/Ho_Chi_Minh',
  isAlwaysOpen = false,
  shopBellEnabled = true,
  isOpenStatus,
  onChangeWeeklyHours,
  onChangeAlwaysOpen,
  onChangeShopBell,
}) => {
  const [activeDay, setActiveDay] = useState<string>('1');

  // Helper to get day schedule
  const getDaySchedule = (dayOfWeek: number): DayScheduleData => {
    const found = weeklyHours?.find(item => item.dayOfWeek === dayOfWeek);
    return found || {dayOfWeek, slots: []};
  };

  // Toggle open/closed for a specific day
  const handleToggleDay = (dayOfWeek: number, isOpenDay: boolean) => {
    const updated = [...(weeklyHours || [])];
    const index = updated.findIndex(item => item.dayOfWeek === dayOfWeek);

    if (isOpenDay) {
      const defaultSlots: TimeSlotData[] = [
        {start: 330, end: 780, isOpen: true}, // 05:30 - 13:00 (mặc định phù hợp quán ăn sáng/trưa)
      ];
      if (index >= 0) {
        updated[index] = {
          dayOfWeek,
          slots: updated[index].slots?.length > 0 ? updated[index].slots : defaultSlots,
        };
      } else {
        updated.push({dayOfWeek, slots: defaultSlots});
      }
    } else {
      if (index >= 0) {
        updated[index] = {dayOfWeek, slots: []};
      } else {
        updated.push({dayOfWeek, slots: []});
      }
    }
    onChangeWeeklyHours(updated);
  };

  // Change slot time
  const handleChangeSlotTime = (
    dayOfWeek: number,
    slotIndex: number,
    field: 'start' | 'end',
    val: dayjs.Dayjs | null,
  ) => {
    const minutes = dayjsToMinutes(val);
    const updated = [...(weeklyHours || [])];
    const daySchedule = getDaySchedule(dayOfWeek);
    const slots = [...daySchedule.slots];

    slots[slotIndex] = {
      ...slots[slotIndex],
      [field]: minutes,
    };

    const dayIndex = updated.findIndex(item => item.dayOfWeek === dayOfWeek);
    if (dayIndex >= 0) {
      updated[dayIndex] = {...updated[dayIndex], slots};
    } else {
      updated.push({dayOfWeek, slots});
    }
    onChangeWeeklyHours(updated);
  };

  // Toggle slot active state (isOpen)
  const handleToggleSlotOpen = (dayOfWeek: number, slotIndex: number, isOpen: boolean) => {
    const updated = [...(weeklyHours || [])];
    const daySchedule = getDaySchedule(dayOfWeek);
    const slots = [...daySchedule.slots];

    slots[slotIndex] = {
      ...slots[slotIndex],
      isOpen,
    };

    const dayIndex = updated.findIndex(item => item.dayOfWeek === dayOfWeek);
    if (dayIndex >= 0) {
      updated[dayIndex] = {...updated[dayIndex], slots};
    } else {
      updated.push({dayOfWeek, slots});
    }
    onChangeWeeklyHours(updated);
  };

  // Add a new slot to day
  const handleAddSlot = (dayOfWeek: number) => {
    const updated = [...(weeklyHours || [])];
    const daySchedule = getDaySchedule(dayOfWeek);
    const slots = [...daySchedule.slots];

    const lastSlot = slots[slots.length - 1];
    let newStart = 810; // 13:30
    let newEnd = 1320;  // 22:00

    if (lastSlot) {
      if (lastSlot.end + 60 < 1440) {
        newStart = lastSlot.end + 30;
        newEnd = Math.min(newStart + 240, 1440);
      }
    }

    slots.push({start: newStart, end: newEnd, isOpen: true});

    const dayIndex = updated.findIndex(item => item.dayOfWeek === dayOfWeek);
    if (dayIndex >= 0) {
      updated[dayIndex] = {...updated[dayIndex], slots};
    } else {
      updated.push({dayOfWeek, slots});
    }
    onChangeWeeklyHours(updated);
  };

  // Remove slot from day
  const handleRemoveSlot = (dayOfWeek: number, slotIndex: number) => {
    const updated = [...(weeklyHours || [])];
    const daySchedule = getDaySchedule(dayOfWeek);
    const slots = daySchedule.slots.filter((_, idx) => idx !== slotIndex);

    const dayIndex = updated.findIndex(item => item.dayOfWeek === dayOfWeek);
    if (dayIndex >= 0) {
      updated[dayIndex] = {...updated[dayIndex], slots};
    } else {
      updated.push({dayOfWeek, slots});
    }
    onChangeWeeklyHours(updated);
  };

  // Apply preset to current day or all days
  const handleApplyPreset = (presetSlots: TimeSlotData[], applyToAll: boolean) => {
    const cloned = presetSlots.map(s => ({...s}));
    if (applyToAll) {
      const updated: DayScheduleData[] = [];
      for (let d = 0; d <= 6; d++) {
        updated.push({dayOfWeek: d, slots: cloned.map(s => ({...s}))});
      }
      onChangeWeeklyHours(updated);
    } else {
      const currentDay = parseInt(activeDay, 10);
      const updated = [...(weeklyHours || [])];
      const index = updated.findIndex(item => item.dayOfWeek === currentDay);
      if (index >= 0) {
        updated[index] = {dayOfWeek: currentDay, slots: cloned};
      } else {
        updated.push({dayOfWeek: currentDay, slots: cloned});
      }
      onChangeWeeklyHours(updated);
    }
  };

  // Copy current day's schedule to all other days
  const handleCopyDayToAll = (sourceDayOfWeek: number) => {
    const sourceSchedule = getDaySchedule(sourceDayOfWeek);
    const updated: DayScheduleData[] = [];

    for (let d = 0; d <= 6; d++) {
      updated.push({
        dayOfWeek: d,
        slots: sourceSchedule.slots.map(s => ({
          start: s.start,
          end: s.end,
          isOpen: s.isOpen !== false,
        })),
      });
    }
    onChangeWeeklyHours(updated);
  };

  const currentDayOfWeek = parseInt(activeDay, 10);
  const currentSchedule = getDaySchedule(currentDayOfWeek);
  const isCurrentDayOpen = currentSchedule.slots && currentSchedule.slots.length > 0;
  const currentDayInfo = DAYS.find(d => d.dayOfWeek === currentDayOfWeek);

  return (
    <Collapse
      defaultActiveKey={['schedule']}
      className="shadow-sm mb-4 border border-gray-200 bg-white"
      size="large"
      items={[
        {
          key: 'schedule',
          label: (
            <div className="flex flex-wrap items-center gap-2">
              <ClockCircleOutlined style={{color: '#1677ff', fontSize: 18}} />
              <span className="font-bold text-base text-gray-800">Cấu hình thời gian & Lịch mở cửa quán</span>
              {isOpenStatus !== undefined && (
                <Tag
                  icon={isOpenStatus ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  color={isOpenStatus ? 'success' : 'default'}
                  className="ml-2 font-medium">
                  {isOpenStatus ? 'Quán đang mở cửa' : 'Quán đang đóng cửa'}
                </Tag>
              )}
            </div>
          ),
          extra: onChangeAlwaysOpen && (
            <div
              className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1 rounded-lg"
              onClick={e => e.stopPropagation()}>
              <span className="font-semibold text-xs text-amber-900">Chế độ 24/7 (Luôn mở):</span>
              <Switch
                checked={isAlwaysOpen}
                onChange={onChangeAlwaysOpen}
                checkedChildren="BẬT"
                unCheckedChildren="TẮT"
                size="small"
                style={{backgroundColor: isAlwaysOpen ? '#f59e0b' : undefined}}
              />
            </div>
          ),
          children: (
            <div>
              {/* Alert if Always Open is active */}
              {isAlwaysOpen && (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-4"
                  message="Quán đang bật chế độ 'Luôn mở cửa (24/7)'"
                  description="Cửa hàng sẽ luôn hiển thị mở cửa bất kể khung giờ. Khi muốn quán tự động mở/đóng theo từng ngày trong tuần, hãy tắt nút 'Chế độ 24/7' ở trên."
                />
              )}

              {/* Top Controls: Timezone, Bell & Quick Presets */}
              <Row gutter={[16, 16]} className="mb-4 bg-gray-50 p-4 rounded-lg items-center">
                <Col xs={24} md={12}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-700">Múi giờ chuẩn:</span>
                    <Tag color="blue" className="font-medium px-2 py-0.5">Việt Nam (GMT+7)</Tag>
                    <span className="text-xs text-gray-500">(Asia/Ho_Chi_Minh)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Hệ thống luôn tự động quy đổi sang giờ Việt Nam để xác định trạng thái mở/đóng cửa quán.
                  </p>
                </Col>

                {onChangeShopBell && (
                  <Col xs={24} md={12}>
                    <div className="flex items-center gap-3">
                      <BellOutlined style={{color: shopBellEnabled ? '#52c41a' : '#8c8c8c'}} />
                      <span className="font-semibold text-gray-700">Chuông báo đơn mới:</span>
                      <Switch
                        checked={shopBellEnabled}
                        onChange={onChangeShopBell}
                        checkedChildren="Bật"
                        unCheckedChildren="Tắt"
                      />
                      <span className="text-xs text-gray-500">
                        {shopBellEnabled ? '(Đang bật chuông báo)' : '(Đã tắt chuông)'}
                      </span>
                    </div>
                  </Col>
                )}

                <Col xs={24}>
                  <div className="pt-2 border-t border-gray-200 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                      <ThunderboltOutlined style={{color: '#faad14'}} /> Cài đặt nhanh cho {currentDayInfo?.label}:
                    </span>
                    {PRESETS.map((p, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        onClick={() => handleApplyPreset(p.slots, false)}
                        className="text-xs">
                        {p.name}
                      </Button>
                    ))}
                    <Popconfirm
                      title={`Áp dụng mẫu "Quán ăn sáng (05:30 - 13:00)" cho cả tuần?`}
                      description="Tất cả các ngày từ T2 đến CN sẽ được đặt thành 05:30 - 13:00."
                      onConfirm={() => handleApplyPreset(PRESETS[0].slots, true)}
                      okText="Áp dụng cả tuần"
                      cancelText="Hủy">
                      <Button size="small" type="primary" ghost className="text-xs">
                        Áp dụng 05:30 - 13:00 cả tuần
                      </Button>
                    </Popconfirm>
                  </div>
                </Col>
              </Row>

              {/* Day Selector Tabs */}
              <Tabs
                activeKey={activeDay}
                onChange={setActiveDay}
                type="card"
                items={DAYS.map(day => {
                  const schedule = getDaySchedule(day.dayOfWeek);
                  const hasSlots = schedule.slots && schedule.slots.length > 0;
                  return {
                    key: String(day.dayOfWeek),
                    label: (
                      <span className="flex items-center gap-1.5 px-1">
                        <span
                          style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: hasSlots ? '#52c41a' : '#d9d9d9',
                          }}
                        />
                        <span className="font-medium">{day.label}</span>
                        {hasSlots && (
                          <span className="text-xs text-gray-500 ml-0.5">({schedule.slots.length})</span>
                        )}
                      </span>
                    ),
                  };
                })}
              />

              {/* Active Day Content */}
              <div className="p-4 border border-t-0 border-gray-200 rounded-b-lg bg-white">
                <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-100 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-base text-gray-800">
                      Lịch hoạt động {currentDayInfo?.fullLabel}
                    </span>
                    <Switch
                      checked={isCurrentDayOpen}
                      onChange={checked => handleToggleDay(currentDayOfWeek, checked)}
                      checkedChildren="Mở cửa"
                      unCheckedChildren="Nghỉ cả ngày"
                    />
                    <Tag color={isCurrentDayOpen ? 'blue' : 'default'}>
                      {isCurrentDayOpen ? `${currentSchedule.slots.length} khung giờ` : 'Đóng cửa cả ngày'}
                    </Tag>
                  </div>

                  <Popconfirm
                    title={`Áp dụng lịch của ${currentDayInfo?.label} cho cả tuần?`}
                    description="Tất cả các ngày trong tuần (T2 đến CN) sẽ được sao chép giống hệt ngày này."
                    onConfirm={() => handleCopyDayToAll(currentDayOfWeek)}
                    okText="Sao chép"
                    cancelText="Hủy">
                    <Button icon={<CopyOutlined />} size="small" type="dashed">
                      Sao chép {currentDayInfo?.label} cho cả tuần
                    </Button>
                  </Popconfirm>
                </div>

                {/* Slots List or Closed Message */}
                {isCurrentDayOpen ? (
                  <div>
                    <div className="flex flex-col gap-3">
                      {currentSchedule.slots.map((slot, index) => {
                        const isSlotActive = slot.isOpen !== false;
                        const isInvalid = slot.start >= slot.end;

                        return (
                          <div
                            key={index}
                            className={`flex flex-wrap items-center justify-between p-3 rounded-lg border transition-colors ${
                              isSlotActive ? 'bg-blue-50/50 border-blue-200' : 'bg-gray-50 border-gray-200 opacity-75'
                            }`}>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="font-semibold text-gray-700 w-24">
                                Khung giờ {index + 1}:
                              </span>
                              <Space size="middle">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-500 font-medium">Giờ mở:</span>
                                  <TimePicker
                                    format={format}
                                    value={minutesToDayjs(slot.start)}
                                    disabled={!isSlotActive}
                                    onChange={val =>
                                      handleChangeSlotTime(currentDayOfWeek, index, 'start', val)
                                    }
                                    allowClear={false}
                                  />
                                </div>
                                <span className="text-gray-400 font-bold">→</span>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-500 font-medium">Giờ đóng:</span>
                                  <TimePicker
                                    format={format}
                                    value={minutesToDayjs(slot.end)}
                                    disabled={!isSlotActive}
                                    onChange={val =>
                                      handleChangeSlotTime(currentDayOfWeek, index, 'end', val)
                                    }
                                    allowClear={false}
                                  />
                                </div>
                              </Space>

                              {isInvalid && (
                                <Tag color="error">Giờ mở phải trước giờ đóng!</Tag>
                              )}
                            </div>

                            <div className="flex items-center gap-3 mt-2 sm:mt-0">
                              <Tooltip title={isSlotActive ? 'Tạm tắt ca này' : 'Bật lại ca này'}>
                                <Switch
                                  size="small"
                                  checked={isSlotActive}
                                  onChange={val => handleToggleSlotOpen(currentDayOfWeek, index, val)}
                                  checkedChildren="Mở"
                                  unCheckedChildren="Tắt"
                                />
                              </Tooltip>

                              <Button
                                danger
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => handleRemoveSlot(currentDayOfWeek, index)}>
                                Xóa
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => handleAddSlot(currentDayOfWeek)}
                      className="mt-3 w-full border-blue-300 text-blue-600 hover:text-blue-700">
                      Thêm khung giờ mở cửa cho {currentDayInfo?.label}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-gray-500 mb-3 font-medium">
                      Cửa hàng được cài đặt đóng cửa vào {currentDayInfo?.fullLabel}.
                    </p>
                    <Button
                      type="primary"
                      ghost
                      icon={<PlusOutlined />}
                      onClick={() => handleToggleDay(currentDayOfWeek, true)}>
                      Mở cửa vào {currentDayInfo?.label}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ),
        },
      ]}
    />
  );
};

export default ShopWeeklyScheduleManager;
